import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import CollapsibleSidebar from "../Components/CollapsibleSidebar";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import { Toaster, toast } from "react-hot-toast";
import { useNavigate, useLocation } from "react-router-dom";
import { fetchUserData } from "../Services/APIService"; // Adjust path to your user API
import { User } from "../types/user.types"; // Adjust path to your User interface

interface UserScore {
  userId: number;
  userName: string;
  score: number;
}

const UserScore: React.FC = () => {
  const [users, setUsers] = useState<UserScore[]>([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [scores, setScores] = useState<{ [key: number]: number }>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [toggleCleared, setToggleCleared] = useState(false);
  const [searchText, setSearchText] = useState<string>("");
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Fetch users and initialize scores
  const fetchAndInitializeData = async () => {
    setLoading(true);
    try {
      const userData = await fetchUserData();
      const filteredUsers = userData.filter((user) => user.userTypeId === 4);
      const userScores: UserScore[] = filteredUsers.map((user) => ({
        userId: user.userId,
        userName: user.userName,
        score: 1, // Default score of 1
      }));

      const initialScores = userScores.reduce((acc: { [key: number]: number }, user) => {
        acc[user.userId] = 1; // Set default score to 1
        return acc;
      }, {});

      setUsers(userScores);
      setScores(initialScores);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      toast.error(`Failed to load users: ${err instanceof Error ? err.message : "Unknown error"}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAndInitializeData();

    // Handle menuID for sidebar
    const getMenus = async () => {
      try {
        const menuID = location.state?.menuID;
        const previousMenuID = localStorage.getItem("activeMenuID");
        if (menuID !== previousMenuID || menuID === null) {
          localStorage.setItem("activeMenuID", menuID);
        } else {
          localStorage.removeItem("activeMenuID");
        }
      } catch (error) {
        console.error("Error fetching menus:", error);
      }
    };
    getMenus();
  }, [location]);

  const handleScoreChange = (userId: number, value: string) => {
    const numValue = parseInt(value) || 1; // Default to 1 if invalid
    setScores((prev) => ({ ...prev, [userId]: numValue }));
  };

  const handleRowSelected = (state: { selectedRows: any[] }) => {
    setSelectedRows(state.selectedRows);
  };

  const handleClearSelected = () => {
    setToggleCleared(!toggleCleared);
    setSelectedRows([]);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // Placeholder for save API call
      // Example: await saveUserScores(payload);
      toast.success("Scores saved successfully");
      setIsEditMode(false);
      await fetchAndInitializeData();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save scores");
      toast.error(`Save failed: ${err instanceof Error ? err.message : "Unknown error"}`);
    } finally {
      setLoading(false);
    }
  };

  const toggleSidebar = () => {
    setIsSidebarCollapsed((prev) => !prev);
  };

  const columns = [
    {
      name: "User Name",
      selector: (row: UserScore) => row.userName,
      sortable: true,
      cell: (row: UserScore) => (
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <div
            style={{
              width: "24px",
              height: "24px",
              borderRadius: "50%",
              backgroundColor: "rgb(167 133 144)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "10px",
              fontWeight: "bold",
            }}
          >
            {row.userName.charAt(0).toUpperCase()}
          </div>
          <span style={{ fontWeight: "500", color: "#33475b" }}>{row.userName}</span>
        </div>
      ),
      width: "400px",
    },
    {
      name: "Score",
      selector: (row: UserScore) => row.score,
      cell: (row: UserScore) =>
        isEditMode ? (
          <input
            type="number"
            value={scores[row.userId] || 1}
            onChange={(e) => handleScoreChange(row.userId, e.target.value)}
            onKeyDown={(e) => {
              if (["Backspace", "Tab", "ArrowLeft", "ArrowRight", "Delete"].includes(e.key)) {
                return;
              }
              if (!/^\d$/.test(e.key)) {
                e.preventDefault();
              }
            }}
            className="p-1 form-control-custom-tbl"
            disabled={!isEditMode}
            min="1"
          />
        ) : (
          scores[row.userId] || 1
        ),
      sortable: true,
    },
  ];

  const filteredData = users.filter((user) =>
    user.userName.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <div className="wrapper">
      <div>
        <Toaster
          position="top-center"
          reverseOrder={false}
          containerStyle={{
            zIndex: 10000,
          }}
        />
      </div>
      {!isSidebarCollapsed && <CollapsibleSidebar />}
      <div className="main">
        <Navbar onToggleSidebar={toggleSidebar} />
        <div className="main-content-div">
          <div className="main-header-div"></div>
          <main className="content mainContent">
            <div className="container-fluid p-0">
              <div className="row">
                <div className="col-12 col-lg-12">
                  <div className="card">
                    <div className="card-header carHeaderWithBtn">
                      <div className="card-title mb-0">
                        User Score Management
                        {selectedRows.length > 0 && (
                          <span className="badge bg-primary ms-2">
                            {selectedRows.length} selected
                          </span>
                        )}
                      </div>
                      <div>
                        <input
                          type="text"
                          placeholder="Search by User Name"
                          className="p-2 w-full datatableSearchInput"
                          value={searchText}
                          onChange={(e) => setSearchText(e.target.value)}
                        />
                        <button
                          className="demo-btn btn btn-outline-primary btn-sm"
                          onClick={async () => {
                            await fetchAndInitializeData();
                            setIsEditMode(true);
                          }}
                          disabled={isEditMode}
                        >
                          + Edit Scores
                        </button>
                      </div>
                    </div>
                    <div className="card-body p-0">
                      <DataTable
                        key={toggleCleared ? "cleared" : "uncleared"}
                        columns={columns}
                        data={filteredData}
                        pagination
                        paginationPerPage={10}
                        highlightOnHover
                        striped
                        responsive
                        noDataComponent="No users found"
                        selectableRows
                        selectableRowsComponentProps={{
                          className: "custom-checkbox",
                        }}
                        onSelectedRowsChange={handleRowSelected}
                        clearSelectedRows={toggleCleared}
                      />
                    </div>
                    {isEditMode && (
                      <div className="card-footer d-flex justify-content-between">
                        <div></div>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-secondary"
                            onClick={() => {
                              setIsEditMode(false);
                              handleClearSelected();
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            className="btn btn-primary"
                            onClick={handleSave}
                            disabled={loading || !!error}
                          >
                            {loading ? "Saving..." : "Save Scores"}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default UserScore;